import AppCommandBar from 'app/AppCommandBar'
import {
  dateTimeToGraphQLDateTime,
  FlexSpinner,
  Text2,
  TimeAgo,
} from 'components'
import { ResourceManager } from 'components/TimeLine'
import { CurrentFormikContext } from 'context'
import { ErrorsDialog } from 'dialogs/ErrorsDialog'
import { Formik, FormikActions, FormikContext } from 'formik'
import { ProvingDetailsGeneralDeclineProvingForm } from 'forms/ProvingDetailsForm/ProvingDetailsGeneralDeclineProvingForm'
import {
  ProvingDetailsGeneralForm,
  ProvingDetailsGeneralFormSchema,
  ProvingDetailsGeneralFormSchemaShape,
  ProvingDetailsGeneralFormType,
} from 'forms/ProvingDetailsForm/ProvingDetailsGeneralForm'
import { ProvingDetailsSetStatusForm } from 'forms/ProvingDetailsForm/ProvingDetailsSetStatusForm'
import {
  canUpdateProving,
  isInProvingRole,
  omitDeep,
  removeNullProperties,
} from 'helpers'
import { useAuthContext } from 'hooks/useAuthContext'
import { useCommand } from 'hooks/useCommand'
import { useCurrentProving } from 'hooks/useCurrentProving'
import { useDialogState } from 'hooks/useDialogState'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import { Proving, ProvingUserRole, UserRole } from 'model'
import { Label, Panel, PanelType } from 'office-ui-fabric-react'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'theme'
import {
  useGenerateExcelOerviewMutation,
  useUpdateProvingMutation,
  useUpdateProvingStatusMutation,
} from './hooks/index'
import { Page, Section } from './Layout'
import { validate } from './validation'

enum FormType {
  setStatus,
  getResourceManager,
  declineReason,
}
export enum PageGeneralStatus {
  CORRECT,
  INVALID,
  DIRTY,
}
interface PageGeneralProps {
  setPageToValid: React.Dispatch<React.SetStateAction<PageGeneralStatus>>
  pageIsValid: PageGeneralStatus
}
const ProvingDetailsGeneralFormCommands: React.FC<{
  formik: FormikContext<ProvingDetailsGeneralFormType>
}> = ({ formik, children }) => {
  const { t } = useTranslation(['ProvingDetailsView', 'common'])
  useCommand(
    {
      key: 'save',
      text: t('action.save'),
      iconProps: { iconName: 'Save' },
      priority: 0,
      disabled: (() => {
        // dbg(formik.dirty,'FORMIK IS DIRTY')
        // dbg(formik.isValid,'FORMIK IS VALID')
        // dbg(formik.isSubmitting,'FORMIK IS SUBMITTING')
        if (formik.isSubmitting || !formik.isValid) {
          // console.log('FOrmik is not valid or submitting')
          return true
        } else {
          if (formik.dirty) {
            // console.log('FOrmik is valid, not submitting and dirty')
            return false
          } else {
            // console.log('FOrmik is valid, not submitting and not dirty')
            return true
          }
        }
      })(),
      onClick: () => formik.submitForm(),
    },
    [
      formik.submitForm,
      formik.isSubmitting,
      formik.isValid,
      formik.dirty,
      formik.initialValues,
      formik.isInitialValid,
    ]
  )
  return <>{children}</>
}

export const PageGeneral: React.FC<PageGeneralProps> = ({
  setPageToValid,
  pageIsValid,
}) => {
  const [proving, refetchProving] = useCurrentProving()
  const { t } = useTranslation()
  const [updateProving] = useUpdateProvingMutation()
  const [updateProvingStatus] = useUpdateProvingStatusMutation()
  const [generateExcelOverview] = useGenerateExcelOerviewMutation()
  const [panelState, showPanel, hidePanel] = useFormikPanelState()
  const [errorsDialogState, showErrorsDialog, ,] = useDialogState()
  const [formState, setFormState] = React.useState()
  const [excelOverviewLoading, setExcelOverviewLoading] = React.useState(false)
  const theme = useTheme()

  const [statusErrors, setStatusErrors] = React.useState()
  // const statusChange = proving.statusChange || {
  //   resolved: proving.modified,
  //   resolvedBy: proving.modifiedBy,
  // }

  const isProvingAdmin = isInProvingRole(proving, ProvingUserRole.Administrator)
  const isResourceManager = isInProvingRole(
    proving,
    ProvingUserRole.ResourceManager
  )

  const auth = useAuthContext()
  const isExternal = auth.hasUserRole(UserRole.Hrg)

  const clearingIds =
    proving.provingGround && proving.provingGround.clearing
      ? proving.provingGround.clearing.map((e) => e._id)
      : []

  const generalFormValidationSchema = new ProvingDetailsGeneralFormSchema()
  const pickFormValues = React.useCallback((p: Proving) => {
    const keys = [
      'title',
      'description',
      'dateTo',
      'dateFrom',
      'department',
      'responsible',
      'deputy',
      'editor',
      'resourceManager',
      'hrgEditor',
      'dvtWvtCheckDisabled',
      'headOfProvingCheckDisabled',
      'multiSelectEditorCheckEnabled',
      'serviceTeamIsEditing',
      'hrgIsEditing',
      'multiSelectResponsibleCheckEnabled',
      'adminsOnlyResponsibleCheckEnabled',
      'multiSelectSubstituteCheckEnabled',
      'adminsOnlySubstituteCheckEnabled',
      'enableGroups',
      'hasIncompleteBookings',
      'effectiveSumOfCosts',
      'supportNeeds',
    ]

    const pickedValues = keys.reduce((acc, key) => {
      acc[key] = omitDeep(p[key], '__typename')
      return acc
    }, {})

    return {
      ...pickedValues,
      orderIdDistribution: omitDeep(p.orderIdDistribution, '__typename'),
    } as ProvingDetailsGeneralFormType
  }, [])

  useCommand(
    {
      key: 'finishProvingRegistration',
      text: 'Anmeldung abschließen',
      iconProps: { iconName: 'MailReplyMirrored' },
      hidden: proving.status !== 1 || isExternal!,
      disabled: !canUpdateProving(proving),
      priority: 10,
      onClick: () => {
        const statErrors = validate(proving)
        setStatusErrors(statErrors)

        if (statErrors.length === 0) {
          updateProvingStatus({
            variables: { id: proving.id, status: 2 },
          }).then(refetchProving)
        } else {
          // display dialog with all errors
          showErrorsDialog()
        }
      },
    },
    [proving]
  )
  useCommand(
    {
      key: 'registrationOk',
      text: 'Anmeldung wurde geprüft',
      iconProps: { iconName: 'Accept' },
      hidden:
        proving.status !== 2 ||
        isExternal! ||
        (!clearingIds.includes(auth.user ? auth.user._id : '') &&
          !auth.hasUserRole(UserRole.SystemAdministrator)),
      disabled: !canUpdateProving(proving),
      priority: 10,
      onClick: () => {
        updateProvingStatus({
          variables: {
            id: proving.id,
            status: 3,
            declineReason: '',
          },
        }).then(refetchProving)
      },
    },
    [proving]
  )
  useCommand(
    {
      key: 'registrationDeclined',
      text: 'Anmeldung zurückweisen',
      iconProps: { iconName: 'CalculatorMultiply' },
      hidden:
        proving.status !== 2 ||
        isExternal! ||
        (!clearingIds.includes(auth.user ? auth.user._id : '') &&
          !auth.hasUserRole(UserRole.SystemAdministrator)),
      disabled: !canUpdateProving(proving),
      priority: 20,
      onClick: () => {
        showPanel({
          status: proving.status,
        })
        setFormState({ formType: FormType.declineReason })
        refetchProving()
      },
    },
    [proving]
  )
  useCommand(
    {
      key: 'editingFinished',
      text: 'Bearbeitung abgeschlossen + Mail an Verantwortlichen',
      iconProps: { iconName: 'ActivateOrders' },
      hidden: proving.status !== 3 || !!proving.hasIncompleteBookings,
      disabled: !canUpdateProving(proving),
      priority: 10,
      onClick: () => {
        const statErrors = validate(proving)
        setStatusErrors(statErrors)
        if (statErrors.length === 0) {
          updateProvingStatus({
            variables: { id: proving.id, status: 4 },
          }).then(refetchProving)
        } else {
          // display dialog with all errors
          showErrorsDialog()
        }
      },
    },
    [proving]
  )
  useCommand(
    {
      key: 'editingFinishedOpenBookings',
      text: 'Bearbeitung abgeschlossen (ohne Mail an Antragsteller)',
      iconProps: { iconName: 'ActivateOrders' },
      hidden: proving.status !== 3 || !proving.hasIncompleteBookings,
      disabled: !canUpdateProving(proving),
      priority: 10,
      onClick: () => {
        const statErrors = validate(proving)
        setStatusErrors(statErrors)

        if (statErrors.length === 0) {
          updateProvingStatus({
            variables: { id: proving.id, status: 4 },
          }).then(refetchProving)
        } else {
          // display dialog with all errors
          showErrorsDialog()
        }
      },
    },
    [proving]
  )
  useCommand({
    key: 'sendChanges',
    text: 'Änderungen bitte absenden',
    iconProps: { iconName: 'ActivateOrders' },
    hidden: proving.status !== 5 || !!isExternal,
    disabled: !canUpdateProving(proving),
    priority: 10,
    onClick: () => {
      const statErrors = validate(proving)
      setStatusErrors(statErrors)

      if (statErrors.length === 0) {
        if (
          proving &&
          proving.provingGround &&
          proving.provingGround.skipClearing
        ) {
          updateProvingStatus({
            variables: { id: proving.id, status: 7 },
          }).then(refetchProving)
        } else {
          updateProvingStatus({
            variables: { id: proving.id, status: 6 },
          }).then(refetchProving)
        }
      } else {
        // display dialog with all errors
        showErrorsDialog()
      }
    },
  })
  useCommand(
    {
      key: 'changesOK',
      text: 'Änderungen wurden geprüft',
      iconProps: { iconName: 'Accept' },
      hidden:
        proving.status !== 6 ||
        !!isExternal ||
        (!clearingIds.includes(auth.user ? auth.user._id : '') &&
          !auth.hasUserRole(UserRole.SystemAdministrator)),
      disabled: !canUpdateProving(proving),
      priority: 10,
      onClick: () => {
        updateProvingStatus({
          variables: {
            id: proving.id,
            status: 7,
            declineReason: '',
          },
        }).then(refetchProving)
      },
    },
    [proving]
  )
  useCommand(
    {
      key: 'changesDeclined',
      text: 'Änderungen zurückweisen',
      iconProps: { iconName: 'CalculatorMultiply' },
      hidden:
        proving.status !== 6 ||
        isExternal! ||
        (!clearingIds.includes(auth.user ? auth.user._id : '') &&
          !auth.hasUserRole(UserRole.SystemAdministrator)),
      disabled: !canUpdateProving(proving),
      priority: 20,
      onClick: () => {
        showPanel({
          status: proving.status,
        })
        setFormState({ formType: FormType.declineReason })
        refetchProving()
      },
    },
    [proving]
  )
  useCommand(
    {
      key: 'editingChangesFinished',
      text: 'Änderungsbearbeitung abgeschlossen + Mail an Verantwortlichen',
      iconProps: { iconName: 'ActivateOrders' },
      hidden: proving.status !== 7 || !!proving.hasIncompleteBookings,
      disabled: !canUpdateProving(proving),
      priority: 10,
      onClick: () => {
        const statErrors = validate(proving)
        setStatusErrors(statErrors)
        if (statErrors.length === 0) {
          const authorizedEditors: any[] = []

          proving &&
            proving.editor &&
            proving.editor.forEach((e) => {
              // tslint:disable-next-line: no-unused-expression
              proving &&
                proving.provingGround &&
                proving.provingGround.authorizedEditors &&
                proving.provingGround.authorizedEditors.forEach((ae) => {
                  if (e._id === ae._id) {
                    authorizedEditors.push(e._id)
                  }
                })
            })

          proving &&
          proving.provingGround &&
          proving.provingGround.deleteEditor === true
            ? updateProvingStatus({
                variables: {
                  id: proving.id,
                  status: 4,
                  editor: authorizedEditors,
                },
              }).then(refetchProving)
            : updateProvingStatus({
                variables: { id: proving.id, status: 4 },
              }).then(refetchProving)
        } else {
          // display dialog with all errors
          showErrorsDialog()
        }
      },
    },
    [proving]
  )
  useCommand(
    {
      key: 'editingChangesFinishedOpenBookings',
      text: 'Änderungsbearbeitung abgeschlossen (ohne Mail an Verantwortlichen)',
      iconProps: { iconName: 'ActivateOrders' },
      hidden: proving.status !== 7 || !proving.hasIncompleteBookings,
      disabled: !canUpdateProving(proving),
      priority: 10,
      onClick: () => {
        const statErrors = validate(proving)
        setStatusErrors(statErrors)

        if (statErrors.length === 0) {
          updateProvingStatus({
            variables: { id: proving.id, status: 4 },
          }).then(refetchProving)
        } else {
          // display dialog with all errors
          showErrorsDialog()
        }
      },
    },
    [proving]
  )
  useCommand(
    {
      key: 'setStatus',
      text: t('field.proving.setStatus'),
      iconProps: { iconName: 'Touch' },
      priority: 100,
      hidden: !isProvingAdmin && !isResourceManager,
      disabled: !isProvingAdmin && !isResourceManager,
      onClick: () => {
        showPanel({
          status: proving.status,
        })
        setFormState({ formType: FormType.setStatus })
        refetchProving()
      },
    },
    [proving]
  )
  useCommand(
    {
      key: 'getResourceManager',
      text: t('field.proving.getResourceManager'),
      iconProps: { iconName: 'AnalyticsReport' },
      hidden: true, // isExternal!,
      disabled: !canUpdateProving(proving),
      priority: 100,
      onClick: () => {
        showPanel({
          status: proving.status,
        })
        setFormState({ formType: FormType.getResourceManager })
      },
    },
    [proving]
  )
  useCommand(
    {
      key: 'generateOverview',
      text: t('field.participantOverview.label'),
      iconProps: { iconName: 'ExcelDocument' },
      farCommand: true,
      onClick: () => {
        setExcelOverviewLoading(true)
        generateExcelOverview({ variables: { id: proving.id } }).then((res) => {
          const downloadLink = res.data ? res.data.excelOverview : undefined
          setExcelOverviewLoading(false)
          if (downloadLink && downloadLink !== '') {
            window.location.href = downloadLink
          }
        })
      },
    },
    [proving, setExcelOverviewLoading]
  )

  const handleSubmit = React.useCallback(
    async (
      values: ProvingDetailsGeneralFormType,
      actions: FormikActions<ProvingDetailsGeneralFormType>
    ) => {
      actions.setSubmitting(true)

      const updatedValues: ProvingDetailsGeneralFormType = removeNullProperties(
        {
          ...values,
        }
      )
      updatedValues.department = updatedValues.department
        ? updatedValues.department.toUpperCase()
        : undefined

      const { errors } = (updatedValues as any).status
        ? await updateProvingStatus({
            variables: {
              id: proving.id,
              status: (updatedValues as any).status,
            },
          })
        : await updateProving({
            variables: {
              id: proving.id,
              update: updatedValues,
            },
          })
      if (errors) {
        throw errors
      }
      await refetchProving()
      localStorage.removeItem(proving._id)
      actions.resetForm()
      actions.resetForm()
      localStorage.removeItem(proving._id)
      actions.setSubmitting(false)
      hidePanel()
    },
    [proving, updateProving, refetchProving, hidePanel, updateProvingStatus]
  )

  const handleDeclineSubmit = React.useCallback(
    async (
      values: ProvingDetailsGeneralFormType,
      actions: FormikActions<ProvingDetailsGeneralFormType>
    ) => {
      actions.setSubmitting(true)

      await updateProving({
        variables: {
          id: proving.id,
          update: {
            declineReason: values.declineReason,
            declined: dateTimeToGraphQLDateTime(new Date(Date.now())),
            declinedBy: auth.user && auth.user._id,
          },
        },
      })

      const { errors } = await updateProvingStatus({
        variables: {
          id: proving.id,
          status: 1,
        },
      })
      if (errors) {
        throw errors
      }
      await refetchProving()

      actions.setSubmitting(false)
      hidePanel()
    },
    [
      proving,
      updateProving,
      updateProvingStatus,
      hidePanel,
      refetchProving,
      auth.user,
    ]
  )

  return (
    <Page>
      <AppCommandBar />
      <FlexSpinner
        paddingTop={2}
        loading={excelOverviewLoading}
        label={t('field.participantOverview.flexSpinner')}
        labelPosition="right"
      />
      {/* <Section
        label={t('section.status.header')}
        stack={{
          horizontal: true,
          wrap: true,
          tokens: { childrenGap: '0 4' },
          disableShrink: true,
        }}
      >
        <StatusText status={proving!.status || 0} />
        <Text2 color="neutralSecondary">
          gesetzt von {statusChange.resolvedBy!.name}
          &nbsp;
          <TimeAgo date={statusChange.resolved!} />
        </Text2>
      </Section> */}
      {proving.declineReason && (
        <Section
          label={t('Diese Erprobung wurde zurückgewiesen')}
          backgroundColor={theme.palette.themeLighter}
        >
          <Label>Begründung:</Label>
          <Text2>{proving.declineReason}</Text2>
          <Text2 color="neutralSecondary">
            Zurückgewiesen von {proving.declinedBy && proving.declinedBy.name}
            &nbsp;
            <TimeAgo date={proving.declined} />
          </Text2>
        </Section>
      )}
      {formState !== undefined && (
        <Panel
          type={PanelType.extraLarge}
          headerText={t('panel.resourceManager.title')}
          closeButtonAriaLabel={t('action.close')}
          isLightDismiss={true}
          onDismiss={() => setFormState({ formType: undefined })}
          isOpen={formState.formType === FormType.getResourceManager}
        >
          <ResourceManager label={'Label'} />
        </Panel>
      )}
      <Formik
        onSubmit={handleSubmit}
        initialValues={pickFormValues(proving)}
        enableReinitialize={true}
        validationSchema={generalFormValidationSchema}
        isInitialValid={() => {
          const generalSchema = ProvingDetailsGeneralFormSchemaShape()
          const isValid = generalSchema.isValidSync(pickFormValues(proving))
          // dbg(isValid,'IsInitialValid')
          return isValid
        }}
      >
        {(formik) => (
          <FlexSpinner loading={formik.isSubmitting}>
            <ProvingDetailsGeneralFormCommands formik={formik}>
              <CurrentFormikContext.Provider value={formik}>
                <ProvingDetailsGeneralForm
                  proving={proving}
                  formik={formik}
                  setPageToValid={setPageToValid}
                  pageIsValid={pageIsValid}
                />
              </CurrentFormikContext.Provider>
            </ProvingDetailsGeneralFormCommands>
          </FlexSpinner>
        )}
      </Formik>

      {formState !== undefined && formState.formType === FormType.setStatus && (
        <FormikPanel {...panelState} onSubmit={handleSubmit}>
          <ProvingDetailsSetStatusForm />
        </FormikPanel>
      )}
      {formState !== undefined &&
        formState.formType === FormType.declineReason && (
          <FormikPanel {...panelState} onSubmit={handleDeclineSubmit}>
            <ProvingDetailsGeneralDeclineProvingForm />
          </FormikPanel>
        )}
      <ErrorsDialog
        {...errorsDialogState}
        minWidth={600}
        errorList={statusErrors as string[]}
      />
      {/* <Section label={t('section.debug.header')}>
        <Section label={t('section.debug.header')}>
        <pre>{JSON.stringify(proving, null, 2)}</pre>
      </Section> */}
    </Page>
  )
}
